import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, X, Book, Music, Video, FileText, Image, User, Navigation, StickyNote, ChevronRight, Loader2, Moon, Award, Users, Bell } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { Tab, UserProfile } from '../types';
import { API_URL } from '../services/apiConfig';

interface SearchResult {
  id: string;
  type: 'ebook' | 'audio' | 'video' | 'blog' | 'note' | 'gallery' | 'navigation' | 'profile' | 'modal';
  title: string;
  subtitle?: string;
  image?: string;
  tab?: Tab;
  data?: any;
  action?: string; // For modal actions like 'ekadashi', 'badges', 'community', 'reminders'
}

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (tab: Tab, data?: any) => void;
  user: UserProfile | null;
  onOpenEkadashi?: () => void;
  onOpenBadges?: () => void;
  onOpenCommunity?: () => void;
  onOpenReminders?: () => void;
}


export const GlobalSearch: React.FC<GlobalSearchProps> = ({ isOpen, onClose, onNavigate, user, onOpenEkadashi, onOpenBadges, onOpenCommunity, onOpenReminders }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { language } = useLanguage();

  // Memoize navigation items to prevent re-creation on every render
  // Includes ALL sidebar items for comprehensive search
  const navigationItems = useMemo<SearchResult[]>(() => [
    // Main Navigation
    { id: 'nav-jaap', type: 'navigation', title: language === 'hi' ? 'जाप' : 'Jaap', subtitle: language === 'hi' ? 'राम नाम काउंटर' : 'Ram Naam Counter', tab: Tab.JAAP },
    { id: 'nav-sangha', type: 'navigation', title: language === 'hi' ? 'संघ' : 'Sangha', subtitle: language === 'hi' ? 'अखंड वर्चुअल संघ' : 'Akhand Virtual Sangha', tab: Tab.AKHAND_JAAP },
    { id: 'nav-scanner', type: 'navigation', title: language === 'hi' ? 'नाम-लेखन स्कैनर' : 'Naam-Lekhan Scanner', subtitle: language === 'hi' ? 'ओसीआर स्कैनर' : 'OCR Scanner', tab: Tab.NIDHI_OCR },
    { id: 'nav-progress', type: 'navigation', title: language === 'hi' ? 'प्रगति' : 'Progress', subtitle: language === 'hi' ? 'डैशबोर्ड' : 'Dashboard', tab: Tab.DASHBOARD },
    
    // Content Sections
    { id: 'nav-ebooks', type: 'navigation', title: language === 'hi' ? 'दिव्य ई-पुस्तकें' : 'Divine E-books', subtitle: language === 'hi' ? 'पीडीएफ पुस्तकें' : 'PDF Books Library', tab: Tab.EBOOKS },
    { id: 'nav-audios', type: 'navigation', title: language === 'hi' ? 'सत्संग ऑडियो' : 'Satsang Audio', subtitle: language === 'hi' ? 'भजन और कीर्तन' : 'Bhajans & Kirtans', tab: Tab.SATSANG_AUDIOS },
    { id: 'nav-videos', type: 'navigation', title: language === 'hi' ? 'दिव्य वीडियो' : 'Divine Videos', subtitle: language === 'hi' ? 'वीडियो गैलरी' : 'Video Gallery', tab: Tab.VIDEOS },
    { id: 'nav-blogs', type: 'navigation', title: language === 'hi' ? 'दिव्य ब्लॉग' : 'Divine Blogs', subtitle: language === 'hi' ? 'आध्यात्मिक लेख' : 'Spiritual Articles', tab: Tab.BLOGS },
    
    // Gallery & Media
    { id: 'nav-gallery', type: 'navigation', title: language === 'hi' ? 'दिव्य गैलरी' : 'Divine Gallery', subtitle: language === 'hi' ? 'फोटो एल्बम' : 'Photo Albums', tab: Tab.ALBUM_GALLERY },
    
    // Spiritual Features
    { id: 'nav-ai-vani', type: 'navigation', title: language === 'hi' ? 'एआई संत वाणी' : 'AI Sant Vani', subtitle: language === 'hi' ? 'एआई आध्यात्मिक मार्गदर्शन' : 'AI Spiritual Guidance', tab: Tab.AI_SANT_VANI },
    { id: 'nav-shloka', type: 'navigation', title: language === 'hi' ? 'आज का श्लोक' : 'Shloka of the Day', subtitle: language === 'hi' ? 'दैनिक ज्ञान' : 'Daily Wisdom', tab: Tab.SHLOKA_OF_DAY },
    { id: 'nav-test', type: 'navigation', title: language === 'hi' ? 'आत्म परीक्षण' : 'Sadhak Self Test', subtitle: language === 'hi' ? 'साधक मूल्यांकन' : 'Spiritual Assessment', tab: Tab.SADHAK_TEST },
    { id: 'nav-insights', type: 'navigation', title: language === 'hi' ? 'साधना अंतर्दृष्टि' : 'Sadhana Insights', subtitle: language === 'hi' ? 'अभ्यास विश्लेषण' : 'Practice Analytics', tab: Tab.SADHANA_INSIGHTS },
    
    // Personal
    { id: 'nav-notes', type: 'navigation', title: language === 'hi' ? 'मेरे नोट्स' : 'My Notes', subtitle: language === 'hi' ? 'व्यक्तिगत नोट्स' : 'Personal Notes', tab: Tab.NOTES },
    { id: 'nav-favorites', type: 'navigation', title: language === 'hi' ? 'पसंदीदा' : 'Favorites', subtitle: language === 'hi' ? 'बुकमार्क' : 'Bookmarked Items', tab: Tab.FAVORITES },
    { id: 'nav-profile', type: 'navigation', title: language === 'hi' ? 'प्रोफाइल' : 'Profile', subtitle: language === 'hi' ? 'खाता सेटिंग्स' : 'Account Settings', tab: Tab.PROFILE },
    
    // Recognition
    { id: 'nav-hall-of-fame', type: 'navigation', title: language === 'hi' ? 'हॉल ऑफ फेम' : 'Hall of Fame', subtitle: language === 'hi' ? 'शीर्ष योगदानकर्ता' : 'Top Contributors', tab: Tab.HALL_OF_FAME },
    { id: 'nav-certificate', type: 'navigation', title: language === 'hi' ? 'प्रमाण पत्र' : 'Certificate', subtitle: language === 'hi' ? 'उपलब्धि डाउनलोड' : 'Download Achievement', tab: Tab.CERTIFICATE },
    
    // Registration & Services
    { id: 'nav-pustika', type: 'navigation', title: language === 'hi' ? 'पुस्तिका रजिस्ट्रेशन' : 'Pustika Registration', subtitle: language === 'hi' ? 'लेखन पुस्तिका ऑर्डर' : 'Order Writing Booklet', tab: Tab.PUSTIKA_REGISTER },
    
    // Information Pages
    { id: 'nav-about', type: 'navigation', title: language === 'hi' ? 'हमारे बारे में' : 'About Us', subtitle: language === 'hi' ? 'हमारा मिशन' : 'Our Mission', tab: Tab.ABOUT_US },
    { id: 'nav-contact', type: 'navigation', title: language === 'hi' ? 'संपर्क करें' : 'Contact Us', subtitle: language === 'hi' ? 'संपर्क में रहें' : 'Get in Touch', tab: Tab.CONTACT_US },
    
    // Modal Items (Special features that open as modals) - Using type 'navigation' with action field
    { id: 'modal-ekadashi', type: 'navigation', title: language === 'hi' ? 'एकादशी कैलेंडर' : 'Ekadashi Calendar', subtitle: language === 'hi' ? 'एकादशी व्रत तिथियां' : 'Ekadashi Fasting Dates', action: 'ekadashi' },
    { id: 'modal-badges', type: 'navigation', title: language === 'hi' ? 'उपलब्धि बैज' : 'Achievement Badges', subtitle: language === 'hi' ? 'आपकी उपलब्धियां' : 'Your Achievements', action: 'badges' },
    { id: 'modal-community', type: 'navigation', title: language === 'hi' ? 'सामूहिक साधना' : 'Community Leaderboard', subtitle: language === 'hi' ? 'लीडरबोर्ड' : 'Rankings & Stats', action: 'community' },
    { id: 'modal-reminders', type: 'navigation', title: language === 'hi' ? 'साधना रिमाइंडर' : 'Sadhana Reminders', subtitle: language === 'hi' ? 'दैनिक अलार्म' : 'Daily Alarms', action: 'reminders' },
  ], [language]);

  // Reset state when closed
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
      setHasSearched(false);
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Search function - optimized to not cause blinking
  const performSearch = useCallback(async (searchQuery: string) => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!searchQuery.trim()) {
      setResults([]);
      setHasSearched(false);
      setIsLoading(false);
      return;
    }

    setHasSearched(true);
    const searchResults: SearchResult[] = [];
    const q = searchQuery.toLowerCase();

    // 1. Search Navigation Items (instant - no loading needed)
    navigationItems.forEach(nav => {
      if (nav.title.toLowerCase().includes(q) || nav.subtitle?.toLowerCase().includes(q)) {
        searchResults.push(nav);
      }
    });

    // 2. Search Profile
    if (user && (
      user.username?.toLowerCase().includes(q) ||
      user.displayName?.toLowerCase().includes(q) ||
      user.email?.toLowerCase().includes(q) ||
      'profile'.includes(q) ||
      'account'.includes(q) ||
      'प्रोफाइल'.includes(q)
    )) {
      searchResults.push({
        id: 'profile-user',
        type: 'profile',
        title: user.displayName || user.username || 'Profile',
        subtitle: user.email || 'View your profile',
        image: user.avatar,
        tab: Tab.PROFILE
      });
    }

    // Show immediate results from navigation
    setResults([...searchResults]);

    // 3. Search API content (in background - don't block UI)
    abortControllerRef.current = new AbortController();
    setIsLoading(true);

    try {
      const signal = abortControllerRef.current.signal;
      
      const fetchWithTimeout = (url: string) => 
        fetch(url, { signal }).then(r => r.ok ? r.json() : []).catch(() => []);

      const [ebooksData, audiosData, videosData, blogsData, galleryData] = await Promise.all([
        fetchWithTimeout(`${API_URL}/api/ebooks`),
        fetchWithTimeout(`${API_URL}/api/audios`),
        fetchWithTimeout(`${API_URL}/api/videos`),
        fetchWithTimeout(`${API_URL}/api/blogs`),
        fetchWithTimeout(`${API_URL}/api/gallery/images`),
      ]);

      const apiResults: SearchResult[] = [];

      // Ebooks
      if (Array.isArray(ebooksData)) {
        ebooksData.forEach((ebook: any) => {
          if (ebook.title?.toLowerCase().includes(q) || ebook.author?.toLowerCase().includes(q)) {
            apiResults.push({
              id: `ebook-${ebook.id}`,
              type: 'ebook',
              title: ebook.title,
              subtitle: ebook.author || ebook.category,
              image: ebook.coverImage,
              tab: Tab.EBOOKS,
              data: ebook
            });
          }
        });
      }

      // Audios
      if (Array.isArray(audiosData)) {
        audiosData.forEach((audio: any) => {
          if (audio.title?.toLowerCase().includes(q) || audio.category?.toLowerCase().includes(q)) {
            apiResults.push({
              id: `audio-${audio.id}`,
              type: 'audio',
              title: audio.title,
              subtitle: audio.category || audio.duration,
              image: audio.thumbnail,
              tab: Tab.SATSANG_AUDIOS,
              data: audio
            });
          }
        });
      }

      // Videos
      if (Array.isArray(videosData)) {
        videosData.forEach((video: any) => {
          if (video.title?.toLowerCase().includes(q) || video.category?.toLowerCase().includes(q)) {
            apiResults.push({
              id: `video-${video.id}`,
              type: 'video',
              title: video.title,
              subtitle: video.category,
              image: video.thumbnail,
              tab: Tab.VIDEOS,
              data: video
            });
          }
        });
      }

      // Blogs
      if (Array.isArray(blogsData)) {
        blogsData.forEach((blog: any) => {
          if (blog.title?.toLowerCase().includes(q) || blog.content?.toLowerCase().includes(q)) {
            apiResults.push({
              id: `blog-${blog.id}`,
              type: 'blog',
              title: blog.title,
              subtitle: blog.excerpt || blog.content?.substring(0, 50) + '...',
              image: blog.coverImage,
              tab: Tab.BLOGS,
              data: blog
            });
          }
        });
      }

      // Gallery
      if (Array.isArray(galleryData)) {
        galleryData.forEach((img: any) => {
          if (img.title?.toLowerCase().includes(q) || img.category?.toLowerCase().includes(q)) {
            apiResults.push({
              id: `gallery-${img.id}`,
              type: 'gallery',
              title: img.title || 'Image',
              subtitle: img.category,
              image: img.imageUrl || img.url,
              tab: Tab.ALBUM_GALLERY,
              data: img
            });
          }
        });
      }

      // Merge results - navigation first, then API results
      setResults(prev => {
        const navResults = prev.filter(r => r.type === 'navigation' || r.type === 'profile');
        return [...navResults, ...apiResults].slice(0, 15);
      });

    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [user, navigationItems]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query);
    }, 250);
    return () => clearTimeout(timer);
  }, [query, performSearch]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      handleSelect(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Handle result selection
  const handleSelect = (result: SearchResult) => {
    // Handle modal actions (check action field first, regardless of type)
    if (result.action) {
      switch (result.action) {
        case 'ekadashi':
          onOpenEkadashi?.();
          break;
        case 'badges':
          onOpenBadges?.();
          break;
        case 'community':
          onOpenCommunity?.();
          break;
        case 'reminders':
          onOpenReminders?.();
          break;
        default:
          // If there's also a tab, navigate to it
          if (result.tab) {
            onNavigate(result.tab, result.data);
          }
      }
    } else if (result.tab) {
      onNavigate(result.tab, result.data);
    }
    onClose();
  };

  // Get icon for result type
  const getIcon = (type: string, action?: string) => {
    // Special icons for modal action items
    if (action) {
      switch (action) {
        case 'ekadashi': return <Moon size={18} className="text-indigo-500" />;
        case 'badges': return <Award size={18} className="text-amber-500" />;
        case 'community': return <Users size={18} className="text-purple-500" />;
        case 'reminders': return <Bell size={18} className="text-blue-500" />;
      }
    }
    switch (type) {
      case 'ebook': return <Book size={18} className="text-amber-600" />;
      case 'audio': return <Music size={18} className="text-purple-600" />;
      case 'video': return <Video size={18} className="text-red-600" />;
      case 'blog': return <FileText size={18} className="text-blue-600" />;
      case 'gallery': return <Image size={18} className="text-green-600" />;
      case 'note': return <StickyNote size={18} className="text-yellow-600" />;
      case 'profile': return <User size={18} className="text-saffron" />;
      case 'navigation': return <Navigation size={18} className="text-gray-500" />;
      default: return <Search size={18} className="text-gray-400" />;
    }
  };

  // Get type label
  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      ebook: language === 'hi' ? 'पुस्तक' : 'E-book',
      audio: language === 'hi' ? 'ऑडियो' : 'Audio',
      video: language === 'hi' ? 'वीडियो' : 'Video',
      blog: language === 'hi' ? 'ब्लॉग' : 'Blog',
      gallery: language === 'hi' ? 'गैलरी' : 'Gallery',
      note: language === 'hi' ? 'नोट' : 'Note',
      profile: language === 'hi' ? 'प्रोफाइल' : 'Profile',
      navigation: language === 'hi' ? 'पेज' : 'Page',
      modal: language === 'hi' ? 'फीचर' : 'Feature',
    };
    return labels[type] || type;
  };

  if (!isOpen) return null;

  return (
    <div 
      ref={dropdownRef}
      className="absolute top-full left-0 right-0 bg-white dark:bg-stone-800 shadow-2xl border-t border-saffron/20 dark:border-stone-700 z-50"
      data-testid="global-search-dropdown"
    >
      <div className="max-w-3xl mx-auto p-3">
        {/* Search Input */}
        <div className="relative">
          <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={language === 'hi' ? 'खोजें... (ebooks, audios, videos, blogs)' : 'Search... (ebooks, audios, videos, blogs)'}
            className="w-full pl-10 pr-10 py-3 bg-gray-50 dark:bg-stone-900 border border-gray-200 dark:border-stone-700 rounded-xl text-gray-800 dark:text-stone-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-saffron/50 focus:border-saffron transition-colors"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            data-testid="global-search-input"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {isLoading && (
              <Loader2 size={16} className="animate-spin text-saffron" />
            )}
            {query && (
              <button
                onClick={() => setQuery('')}
                className="text-gray-400 hover:text-gray-600 p-1"
                type="button"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="mt-2 max-h-[50vh] overflow-y-auto">
          {results.length > 0 ? (
            <div className="space-y-1">
              {results.map((result, index) => (
                <button
                  key={result.id}
                  onClick={() => handleSelect(result)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                    index === selectedIndex 
                      ? 'bg-saffron/10 dark:bg-saffron/20' 
                      : 'hover:bg-gray-50 dark:hover:bg-stone-700'
                  }`}
                  data-testid={`search-result-${result.id}`}
                >
                  {/* Image or Icon */}
                  <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-stone-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {result.image ? (
                      <img src={result.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      getIcon(result.type, result.action)
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 text-left min-w-0">
                    <p className="font-semibold text-gray-800 dark:text-stone-200 truncate text-sm">
                      {result.title}
                    </p>
                    {result.subtitle && (
                      <p className="text-xs text-gray-500 dark:text-stone-400 truncate">
                        {result.subtitle}
                      </p>
                    )}
                  </div>

                  {/* Type Badge */}
                  <span className="text-[9px] font-bold uppercase px-2 py-0.5 bg-gray-100 dark:bg-stone-700 text-gray-500 dark:text-stone-400 rounded-full flex-shrink-0">
                    {getTypeLabel(result.type)}
                  </span>

                  <ChevronRight size={14} className="text-gray-300 flex-shrink-0" />
                </button>
              ))}
            </div>
          ) : hasSearched && query ? (
            <div className="text-center py-6 text-gray-500">
              <Search size={28} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">{language === 'hi' ? 'कोई परिणाम नहीं मिला' : 'No results found'}</p>
            </div>
          ) : !query ? (
            <div className="text-center py-4 text-gray-400">
              <p className="text-sm">{language === 'hi' ? 'खोजने के लिए टाइप करें' : 'Start typing to search'}</p>
            </div>
          ) : null}
        </div>

        {/* Quick Links - only show when no query */}
        {!query && (
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-stone-700">
            <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">
              {language === 'hi' ? 'त्वरित लिंक' : 'Quick Links'}
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                { label: language === 'hi' ? 'ई-पुस्तकें' : 'E-books', tab: Tab.EBOOKS },
                { label: language === 'hi' ? 'ऑडियो' : 'Audios', tab: Tab.SATSANG_AUDIOS },
                { label: language === 'hi' ? 'वीडियो' : 'Videos', tab: Tab.VIDEOS },
                { label: language === 'hi' ? 'ब्लॉग' : 'Blogs', tab: Tab.BLOGS },
                { label: language === 'hi' ? 'गैलरी' : 'Gallery', tab: Tab.ALBUM_GALLERY },
                { label: language === 'hi' ? 'एआई वाणी' : 'AI Vani', tab: Tab.AI_SANT_VANI },
                { label: language === 'hi' ? 'नोट्स' : 'Notes', tab: Tab.NOTES },
                { label: language === 'hi' ? 'प्रोफाइल' : 'Profile', tab: Tab.PROFILE },
              ].map(link => (
                <button
                  key={link.tab}
                  onClick={() => { onNavigate(link.tab); onClose(); }}
                  className="px-3 py-1 text-xs bg-gray-100 dark:bg-stone-700 text-gray-600 dark:text-stone-300 rounded-full hover:bg-saffron/20 hover:text-saffron transition-colors"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
